<script setup lang="ts">
import { computed } from 'vue'

import BetaHelpContactCard from '@/components/beta-dashboard/molecules/BetaHelpContactCard.vue'
import BetaPanelHeading from '@/components/beta-dashboard/molecules/BetaPanelHeading.vue'
import { useApplicationStore } from '@/stores/application'

const applicationStore = useApplicationStore()

const email = computed(() => String(applicationStore.contactEmail || '').trim())
const phone = computed(() => String(applicationStore.contactPhone || '').trim())

const phoneHref = computed(() => {
    const digits = phone.value.replace(/\D/g, '')
    if (!digits) return ''

    return `tel:+${digits.startsWith('55') ? digits : `55${digits}`}`
})
</script>

<template>
    <div class="beta-help">
        <BetaPanelHeading title="Ajuda" />

        <section class="beta-help__content">
            <div class="beta-help__intro">
                <span class="beta-help__mark" aria-hidden="true">?</span>
                <div>
                    <h2>Estamos aqui para ajudar</h2>
                    <p>
                        Fale com o suporte sempre que precisar de orientação para usar ou configurar
                        seu site.
                    </p>
                </div>
            </div>

            <div v-if="email || phone" class="beta-help__contacts">
                <BetaHelpContactCard
                    v-if="email"
                    type="email"
                    label="Atendimento por e-mail"
                    :value="email"
                    :href="`mailto:${email}`"
                />
                <BetaHelpContactCard
                    v-if="phone"
                    type="phone"
                    label="Atendimento por telefone"
                    :value="phone"
                    :href="phoneHref"
                />
            </div>

            <div v-else class="beta-help__unavailable">
                Os canais de atendimento ainda não foram configurados.
            </div>

            <div class="beta-help__hours">
                <span class="beta-help__hours-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.6" />
                        <path d="M12 7.5V12l3 2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </span>
                <span>
                    <small>Horário de atendimento</small>
                    <strong>Segunda a sexta, das 08:00 às 17:00</strong>
                </span>
            </div>
        </section>
    </div>
</template>

<style scoped>
.beta-help {
    display: grid;
    gap: 20px;
}

.beta-help__content {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, 0.9fr);
    gap: 12px 18px;
    border: 1px solid #e1e7ef;
    border-radius: 20px;
    background: #fbfcfe;
    padding: 18px;
}

.beta-help__intro {
    display: grid;
    grid-row: span 2;
    grid-template-columns: 58px minmax(0, 1fr);
    align-items: start;
    gap: 14px;
}

.beta-help__mark {
    display: grid;
    width: 58px;
    height: 58px;
    place-items: center;
    border-radius: 17px;
    background: color-mix(in srgb, var(--main-color) 9%, #eef3f8);
    color: var(--main-color);
    font-size: 22px;
    font-weight: 900;
}

.beta-help__intro h2 {
    color: #2d4561;
    font-size: 16px;
    font-weight: 850;
    letter-spacing: -0.02em;
}

.beta-help__intro p {
    max-width: 520px;
    margin-top: 7px;
    color: #718198;
    font-size: 10px;
    line-height: 1.6;
}

.beta-help__contacts {
    display: grid;
    gap: 8px;
}

.beta-help__hours {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr);
    align-items: center;
    gap: 9px;
    border-radius: 13px;
    background: #eef3f8;
    padding: 9px 11px;
}

.beta-help__hours-icon {
    display: grid;
    width: 34px;
    height: 34px;
    place-items: center;
    border-radius: 10px;
    background: #fff;
    color: var(--main-color);
}

.beta-help__hours-icon svg {
    width: 19px;
    height: 19px;
}

.beta-help__hours small,
.beta-help__hours strong {
    display: block;
}

.beta-help__hours small {
    color: #8795a7;
    font-size: 7px;
    font-weight: 800;
    text-transform: uppercase;
}

.beta-help__hours strong {
    margin-top: 2px;
    color: #40566f;
    font-size: 9px;
    font-weight: 850;
}

.beta-help__unavailable {
    border: 1px dashed #d8e1eb;
    border-radius: 13px;
    padding: 16px;
    color: #7b899c;
    font-size: 9px;
    text-align: center;
}

@media (max-width: 820px) {
    .beta-help__content {
        grid-template-columns: 1fr;
    }

    .beta-help__intro {
        grid-row: auto;
    }
}

@media (max-width: 480px) {
    .beta-help__intro {
        grid-template-columns: 1fr;
    }
}
</style>
